"use client";

import { Profiler, useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuIcon } from "lucide-react";
import { PageContent } from "@/components/layout/PageContent";
import Members from "./workspace/Members";
import BillingInformation from "./workspace/Billing";
import Profile from "./user/Profile";
import { useParams, useNavigate } from "react-router-dom";
import General from "./workspace/General";
import Customisation from "./workspace/Customisation";

interface Page {
  id: string;
  name: string;
  render: () => React.ReactNode;
}

const workspacePages: Page[] = [
  { id: "general", name: "General", render: () => <General /> },
  { id: "members", name: "Members", render: () => <Members /> },
  {
    id: "widget-customisation",
    name: "Widget Options",
    render: () => <Customisation />,
  },
  { id: "billing", name: "Billing", render: () => <BillingInformation /> },
];

const userPages: Page[] = [
  { id: "profile", name: "Profile", render: () => <Profile /> },
];

interface SidebarContentProps {
  pages: Page[];
  activePage: string;
  setActivePage: (id: string) => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  pages,
  activePage,
  setActivePage,
}) => {
  return (
    <nav className="space-y-2">
      {pages.map((page) => (
        <Button
          key={page.id}
          variant={activePage === page.id ? "secondary" : "ghost"}
          className="w-full justify-start"
          onClick={() => setActivePage(page.id)}>
          {page.name}
        </Button>
      ))}
    </nav>
  );
};

export default function SettingsPage() {
  const { tab, subtab } = useParams<{ tab: string; subtab: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"workspace" | "user">("user");
  const [activeWorkspacePage, setActiveWorkspacePage] =
    useState<string>("general");
  const [activeUserPage, setActiveUserPage] = useState<string>("profile");

  useEffect(() => {
    if (tab === "workspace" || tab === "user") {
      setActiveTab(tab);
    } else {
      navigate("/settings/workspace", { replace: true });
    }
  }, [tab, navigate]);

  const pages = activeTab === "workspace" ? workspacePages : userPages;
  const activePage =
    activeTab === "workspace" ? activeWorkspacePage : activeUserPage;
  const setActivePage =
    activeTab === "workspace" ? setActiveWorkspacePage : setActiveUserPage;

  const setActivePageWithRedirect = (id: string) => {
    setActivePage(id);
    navigate(`/settings/${activeTab}/${id}`);
  };

  useEffect(() => {
    if (subtab) {
      setActivePage(subtab);
    }
  }, [tab, subtab, setActivePage]);

  const handleTabChange = (value: string) => {
    const pages = value === "workspace" ? workspacePages : userPages;
    navigate(`/settings/${value}/${pages[0].id}`);
  };

  return (
    <PageContent title="Settings">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="user">User Settings</TabsTrigger>
          <TabsTrigger value="workspace">Workspace Settings</TabsTrigger>
        </TabsList>
        <div className="flex flex-col md:flex-row gap-6">
          {tab !== "user" && (
            <aside className="md:w-64 flex-shrink-0">
              {/* Desktop Sidebar */}
              <Card className="hidden md:block">
                <CardContent className="p-4">
                  <SidebarContent
                    pages={pages}
                    activePage={activePage}
                    setActivePage={setActivePageWithRedirect}
                  />
                </CardContent>
              </Card>

              {/* Mobile Menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full md:hidden mb-4">
                    <MenuIcon className="mr-2 h-4 w-4" />
                    Menu
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="py-4">
                  <h2 className="text-lg font-semibold mb-4">
                    {activeTab === "workspace" ? "Workspace" : "User"} Settings
                  </h2>
                  <SidebarContent
                    pages={pages}
                    activePage={activePage}
                    setActivePage={setActivePageWithRedirect}
                  />
                </SheetContent>
              </Sheet>
            </aside>
          )}

          <main className="flex-grow">
            <TabsContent value="workspace" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {
                      workspacePages.find((p) => p.id === activeWorkspacePage)
                        ?.name
                    }
                  </CardTitle>
                  <CardDescription>
                    Manage your workspace preferences and configurations.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  {workspacePages
                    .find((p) => p.id === activeWorkspacePage)
                    ?.render()}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="user" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {userPages.find((p) => p.id === activeUserPage)?.name}
                  </CardTitle>
                  <CardDescription>
                    Manage your personal account settings and preferences.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {userPages.find((p) => p.id === activeUserPage)?.render()}
                </CardContent>
              </Card>
            </TabsContent>
          </main>
        </div>
      </Tabs>
    </PageContent>
  );
}
